import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Brain, CheckCircle2, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuizModal({ open, cityId, item, quizData, onAnswer, onClose }) {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    if (!open || !item) return;
    setPicked(null);

    // If we have pre-fetched data, use it INSTANTLY (no spinner)
    if (quizData) {
      setQuiz(quizData);
      setLoading(false);
      return;
    }

    setQuiz(null);
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/quiz/${cityId}/${item.id}`)
      .then((r) => setQuiz(r.data))
      .catch(() => setQuiz(null))
      .finally(() => setLoading(false));
  }, [open, cityId, item, quizData]);

  const select = (i) => {
    if (picked !== null) return;
    setPicked(i);
    // Delay briefly so the user sees their selection highlighted
    setTimeout(() => {
      onAnswer?.(i);
    }, 400);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="quiz-modal"
        className="bg-[#0A0A0A] border border-[#FF3B30]/50 shadow-[0_0_60px_rgba(255,59,48,0.35)] rounded-sm max-w-2xl"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[#FFCC00] mb-1">
            <Sparkles className="w-4 h-4" />
            <span className="label-kicker">Scavenger Checkpoint</span>
          </div>
          <DialogTitle className="font-heading text-3xl lg:text-4xl">
            {item?.name ?? "Checkpoint"}
          </DialogTitle>
          <DialogDescription className="text-white/60 text-sm">
            Answer to claim this artifact. Server validates — miss it and it stays on the map.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 flex items-center justify-center gap-2 text-white/60">
            <Brain className="w-5 h-5 animate-pulse" />
            <span className="label-kicker">AI brewing a question…</span>
          </div>
        )}

        {quiz && !loading && (
          <div className="mt-2">
            <div className="font-heading text-xl lg:text-2xl leading-snug">{quiz.question}</div>
            <div className="mt-6 grid grid-cols-1 gap-2">
              {quiz.options.map((opt, i) => (
                <button
                  key={i}
                  data-testid={`quiz-option-${i}`}
                  onClick={() => select(i)}
                  disabled={picked !== null}
                  className={`text-left p-4 border transition-all duration-150 flex items-center gap-3 ${
                    picked === i
                      ? "border-[#FFCC00] bg-[#FFCC00]/10"
                      : "border-white/10 hover:border-white/40"
                  }`}
                >
                  <span className="font-mono text-xs text-white/40 w-5">{String.fromCharCode(65 + i)}</span>
                  <span className="flex-1 text-sm">{opt}</span>
                  {picked === i && <CheckCircle2 className="w-5 h-5 text-[#FFCC00]" />}
                </button>
              ))}
            </div>
            {quiz.explanation && (
              <div className="mt-5 p-3 border border-white/10 text-xs text-white/60">
                <div className="label-kicker mb-1">Post-race fact</div>
                {quiz.explanation}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
